import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Phone } from "lucide-react";
import { PageHero, PrimaryButton, Reveal, SectionTag } from "@/components/ui-bits";
import api, { formatApiError } from "@/lib/api";

const inputCls = "w-full rounded-lg border border-tark-line bg-tark-card px-4 py-3 text-sm text-tark-ink placeholder:text-tark-dim focus:border-tark-cyan/60 focus:outline-none focus:ring-1 focus:ring-tark-cyan/40";
const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-tark-dim";

const Contact = () => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const details = [
    { icon: Mail, label: "Email", value: settings?.contact_email || "hello@tarkinnovation.com",
      href: `mailto:${settings?.contact_email || "hello@tarkinnovation.com"}`, testId: "contact-detail-email" },
    { icon: Phone, label: "Phone", value: settings?.phone || "Add via admin panel", href: settings?.phone ? `tel:${settings.phone.replace(/\s/g, "")}` : null, testId: "contact-detail-phone" },
    { icon: MapPin, label: "Headquarters", value: settings?.address || "Add via admin panel", href: null, testId: "contact-detail-address" },
  ];

  return (
    <>
      <PageHero
        eyebrow="Contact"
        lines={["Let's Start", "the Conversation."]}
        sub="Questions, partnerships or a project in mind — reach the TARK team directly. For detailed project scoping, use Start a Project; for roles, visit Careers."
      />
      <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-10" data-testid="contact-page">
        <div className="grid gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            {done ? (
              <div className="rounded-2xl border border-tark-cyan/40 bg-tark-card p-12 text-center" data-testid="contact-success">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-tark-cyan/40 bg-tark-cyan/10">
                  <span className="h-3 w-3 rotate-45 bg-tark-cyan" />
                </div>
                <h2 className="mt-6 font-display text-2xl font-bold">Message received.</h2>
                <p className="mx-auto mt-3 max-w-md text-sm text-tark-mut">
                  Thank you for contacting TARK. Our team will review your message and get back to you.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl border border-tark-line bg-tark-surface/50 p-8 sm:p-10" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="c-name" className={labelCls}>Name *</label>
                    <input id="c-name" name="name" required value={form.name} onChange={set} className={inputCls} data-testid="contact-name" />
                  </div>
                  <div>
                    <label htmlFor="c-email" className={labelCls}>Email *</label>
                    <input id="c-email" name="email" type="email" required value={form.email} onChange={set} className={inputCls} data-testid="contact-email" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="c-company" className={labelCls}>Company</label>
                    <input id="c-company" name="company" value={form.company} onChange={set} className={inputCls} data-testid="contact-company" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="c-message" className={labelCls}>Message *</label>
                    <textarea id="c-message" name="message" rows={6} required value={form.message} onChange={set}
                              className={inputCls} placeholder="How can we help?" data-testid="contact-message" />
                  </div>
                </div>
                <div className="mt-8">
                  <PrimaryButton type="submit" testId="contact-submit">{submitting ? "Sending…" : "Send Message"}</PrimaryButton>
                </div>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-2">
            <div className="rounded-2xl border border-tark-line bg-tark-card p-8 sm:p-10">
              <SectionTag>Reach Us</SectionTag>
              <h2 className="mt-4 font-display text-xl font-bold tracking-tight">Tark Innovation Private Limited</h2>
              <ul className="mt-8 space-y-6">
                {details.map(({ icon: Icon, label, value, href, testId }) => (
                  <li key={label} className="flex items-start gap-4">
                    <span className="rounded-lg border border-tark-blue/40 bg-tark-blue/10 p-2.5 text-tark-cyan">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-tark-dim">{label}</span>
                      {href ? (
                        <a href={href} data-testid={testId} className="mt-1 block text-sm text-tark-ink transition-colors hover:text-tark-cyan">{value}</a>
                      ) : (
                        <span data-testid={testId} className="mt-1 block text-sm text-tark-ink">{value}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              {settings?.branches?.length > 0 && (
                <div className="mt-8 rounded-xl border border-tark-line bg-tark-bg/60 p-5" data-testid="contact-branches">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-tark-cyan">Upcoming Branches</p>
                  <ul className="mt-3 space-y-2">
                    {settings.branches.map((b) => (
                      <li key={b} className="flex items-center gap-2.5 text-sm text-tark-mut">
                        <span className="h-1.5 w-1.5 rotate-45 bg-tark-cyan/70" />{b}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-10 border-t border-tark-line pt-8">
                <p className="text-sm leading-relaxed text-tark-mut">
                  Scoping a larger initiative?{" "}
                  <a href="/start-a-project" data-testid="contact-start-project-link" className="text-tark-cyan hover:underline">
                    Start a Project
                  </a>{" "}
                  gives our engineering leads everything they need to respond precisely.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;
